<template>
  <div class="dashboard">
    <MainNavbar />

    <div class="dashboard-content">
      <!-- Profile load error: do not show dashboard with default values -->
      <div v-if="profileLoadError" class="dashboard-error-state">
        <div class="error-content">
          <i class="fa-solid fa-exclamation-triangle error-icon"></i>
          <h3>Could not load dashboard</h3>
          <p>We couldn't load your seller profile. Please check your connection and try again.</p>
          <button class="retry-btn" @click="retryLoad">
            <i class="fa-solid fa-rotate"></i>
            Try again
          </button>
          <button class="back-btn" @click="$router.push('/ipromiseafrica/resale')">
            <i class="fa-solid fa-arrow-left"></i>
            Back to Third Party
          </button>
        </div>
      </div>

      <!-- Main dashboard content: only when loaded successfully -->
      <template v-else>
      <!-- PROFILE HEADER -->
      <div class="profile-header" data-aos="fade-up">
        <div class="profile-info">
          <div class="profile-avatar">
            <img
              class="profile-img"
              :src="seller.profilePicture || defaultProfilePicture"
              alt="Seller"
              decoding="async"
              @error="handleImageError"
            />
            <div class="online-indicator"></div>
          </div>

          <div class="profile-details">
            <h2 class="seller-name">{{ seller.name || 'Loading...' }}</h2>
            <div class="profile-meta">
              <span class="market-charges">
                <i class="fa-solid fa-tag"></i>
                Market Charges: Free
              </span>
            </div>
            
            <!-- Approval Status Badge -->
            <div class="approval-status" :class="approvalStatusClass">
              <i :class="approvalIcon"></i>
              <span>{{ approvalStatusText }}</span>
              <span v-if="seller.approvalStatus === 'pending'" class="status-note">
                (Account under review)
              </span>
            </div>
          </div>
        </div>

        <div class="profile-actions">
          <div class="online-status">
            <span class="status-dot"></span>
            <p>Online</p>
          </div>
          <button type="button" class="btn primary edit-btn" @click="goToProfileEdit">
            <i class="fa-solid fa-user-pen"></i>
            <span class="btn-text">Edit Profile</span>
          </button>
          <button
            type="button"
            class="btn outline settings-btn"
            title="Account settings"
            aria-label="Account settings"
            @click="goToSettings"
          >
            <i class="fa-solid fa-gear"></i>
            <span class="btn-text">Settings</span>
          </button>
          <button type="button" class="dashboard-btn" @click="refreshDashboard">
            <i class="fa-solid fa-rotate"></i>
          </button>
        </div>
      </div>

      <!-- STATS CARDS -->
      <div class="stats-grid" data-aos="fade-up" data-aos-delay="100">
        <div
          class="stats-card"
          v-for="(item, index) in widgetData"
          :key="item.label"
          :class="{
            'stats-card--disabled': item.comingSoon,
            'stats-card--nav': item.navigateTo,
          }"
          :data-aos="'fade-up'"
          :data-aos-delay="100 + (index * 50)"
          :title="
            item.comingSoon ? 'Coming Soon' : item.navigateTo ? 'Open reviews' : ''
          "
          role="button"
          :tabindex="item.navigateTo ? 0 : -1"
          @click="onStatsCardClick(item)"
          @keydown.enter.prevent="onStatsCardClick(item)"
          @keydown.space.prevent="onStatsCardClick(item)"
        >
          <div class="stats-card-inner">
            <div class="stats-icon" :style="{ backgroundColor: item.color + '20' }">
              <i :class="item.icon" :style="{ color: item.color }"></i>
            </div>
            <div class="stats-content">
              <h3 class="stats-value">{{ item.value }}</h3>
              <p class="stats-label">{{ item.label }}</p>
              <p v-if="item.subtitle" class="stats-sublabel">{{ item.subtitle }}</p>
            </div>
            <div class="stats-trend" v-if="item.trend">
              <i :class="item.trendIcon" :style="{ color: item.trendColor }"></i>
              <span :style="{ color: item.trendColor }">{{ item.trend }}</span>
            </div>
          </div>
          <div v-if="item.comingSoon" class="stats-card-overlay">
            <span class="coming-soon-text">Coming Soon</span>
          </div>
          <div
            v-else-if="item.navigateTo"
            class="stats-card-overlay stats-card-overlay--reviews"
            aria-hidden="true"
          >
            <span class="coming-soon-text">Reviews</span>
            <i class="fa-solid fa-chevron-right reviews-chevron"></i>
          </div>
        </div>
      </div>

      <!-- QUICK ACTIONS -->
      <div class="quick-actions" data-aos="fade-up" data-aos-delay="200">
        <h3 class="section-title">
          <i class="fa-solid fa-bolt"></i>
          Quick Actions
        </h3>
        <div class="actions-grid">
          <button class="action-card" @click="openTab('/ipromiseafrica/resale/product-upload')">
            <div class="action-icon primary">
              <i class="fa-solid fa-plus"></i>
            </div>
            <h4>Add New Product</h4>
            <p>Create a new listing</p>
          </button>
          
          <button class="action-card" @click="openTab('/ipromiseafrica/resale/my-listings')">
            <div class="action-icon secondary">
              <i class="fa-solid fa-boxes"></i>
            </div>
            <h4>Manage Listings</h4>
            <p>View & edit your products</p>
          </button>
          
          <button class="action-card" @click="openTab('/ipromiseafrica/resale/seller-dashboard/views')">
            <div class="action-icon success">
              <i class="fa-solid fa-eye"></i>
            </div>
            <h4>Views</h4>
            <p>Monitor how customers view your listings</p>
          </button>
          
          <button class="action-card" @click="openTab('/ipromiseafrica/resale/seller-dashboard/earnings')">
            <div class="action-icon warning">
              <i class="fa-solid fa-wallet"></i>
            </div>
            <h4>View Earnings</h4>
            <p>Check your balance</p>
          </button>
        </div>
      </div>

      <!-- DASHBOARD TABS -->
      <div class="dashboard-tabs" data-aos="fade-up" data-aos-delay="250">
        <div class="tabs-header">
          <h3 class="section-title">
            <i class="fa-solid fa-table-cells"></i>
            Dashboard Sections
          </h3>
          <div class="tabs-indicator">
            <div class="indicator-dot" v-for="tab in tabs" :key="tab.name"
                 :class="{ active: activeTab === tab.route }"></div>
          </div>
        </div>
        
        <div class="tabs-container">
          <button
            class="tab-card"
            v-for="tab in tabs"
            :key="tab.name"
            :class="{
              active: activeTab === tab.route,
              'tab-card--no-fx': tab.comingSoon
            }"
            @click="onDashboardTabClick(tab)"
          >
            <div class="tab-icon">
              <i :class="tab.icon"></i>
            </div>
            <div class="tab-content">
              <h4>{{ tab.name }}</h4>
              <p>{{ tab.description }}</p>
            </div>
            <i class="fa-solid fa-chevron-right tab-arrow"></i>
          </button>
        </div>
      </div>

      <!-- RECENT ACTIVITY (Placeholder for future implementation) -->
      <div class="recent-activity" data-aos="fade-up" data-aos-delay="300">
        <h3 class="section-title">
          <i class="fa-solid fa-clock-rotate-left"></i>
          Recent Activity
        </h3>
        <div class="activity-placeholder">
          <i class="fa-solid fa-chart-line"></i>
          <p>Your recent activities will appear here</p>
          <small>Orders, messages, and updates</small>
        </div>
      </div>
      </template>
    </div>

    <!-- Main Footer -->
    <MainFooter />

    <!-- Notification -->
    <div v-if="showNotification" class="notification-popup" :class="notification.type">
      <div class="notification-content">
        <i :class="notification.icon"></i>
        <div class="notification-text">
          <h4>{{ notification.title }}</h4>
          <p>{{ notification.message }}</p>
        </div>
        <button @click="showNotification = false" class="close-notification">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import MainNavbar from "../../components/main/headernfooter/header.vue";
import MainFooter from "./Footer.vue";
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import { buildApiUrl } from "@/config/api";
import AOS from 'aos';
import 'aos/dist/aos.css';

export default {
  name: "SellerDashboard",
  loadingMessage: "Please wait while we fetch your data...",
  mixins: [globalLoadingMixin],
  components: { MainNavbar, MainFooter },

  data() {
    return {
      loading: true,
      profileLoadError: false,
      seller: {
        name: "",
        profilePicture: "",
        sellerNumber: "",
        approvalStatus: ""
      },

      stats: {
        active: 0,
        pending: 0,
        earnings: "0",
        orders: 0,
        views: 0,
      },

      listingStats: {
        activeUnsold: 0,
        totalViews: 0,
      },

      tabs: [
        { 
          name: "My Listings", 
          route: "/ipromiseafrica/resale/my-listings", 
          icon: "fa-solid fa-box",
          description: "Manage your products and inventory"
        },
        { 
          name: "Add Product", 
          route: "/ipromiseafrica/resale/product-upload", 
          icon: "fa-solid fa-circle-plus",
          description: "Create new product listings"
        },
        { 
          name: "Reviews", 
          route: "/ipromiseafrica/resale/seller-dashboard/my_reviews", 
          icon: "fa-solid fa-star",
          description: "Collect Feedback from customers"
        },
        { 
          name: "Views", 
          route: "/ipromiseafrica/resale/seller-dashboard/views", 
          icon: "fa-solid fa-eye",
          description: "Check listing perfomance"
        },
        { 
          name: "Messages", 
          route: "/ipromiseafrica/resale/messages", 
          icon: "fa-solid fa-comments",
          description: "Customer inquiries and chats",
          comingSoon: true
        },
        { 
          name: "Settings", 
          route: "/ipromiseafrica/resale/seller-dashboard/settings", 
          icon: "fa-solid fa-gear",
          description: "Account and store preferences"
        },
      ],

      activeTab: "",
      showNotification: false,
      notification: {
        type: "success",
        title: "",
        message: "",
        icon: ""
      },
      
      defaultProfilePicture: "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"
    };
  },

  computed: {
    widgetData() {
      return [
        { 
          label: "Active Listings", 
          value: this.listingStats.activeUnsold || this.stats.active, 
          icon: "fa-solid fa-store",
          color: "#3b82f6",
          trend: "+2",
          trendIcon: "fa-solid fa-arrow-up",
          trendColor: "#10b981",
          comingSoon: false
        },
        { 
          label: "Listing Views", 
          value: (this.listingStats.totalViews || this.stats.views || 0).toLocaleString(), 
          icon: "fa-solid fa-eye",
          color: "#f59e0b",
          trend: null,
          trendIcon: null,
          trendColor: null,
          comingSoon: false
        },
        { 
          label: "Total Earnings", 
          subtitle: "From sold listings",
          value: `KES ${this.formatNumber(this.stats.earnings)}`, 
          icon: "fa-solid fa-coins",
          color: "#10b981",
          trend: "+12%",
          trendIcon: "fa-solid fa-arrow-up",
          trendColor: "#10b981",
          comingSoon: false
        },
        {
          label: "Reviews",
          subtitle: "Ratings & feedback",
          value: "View",
          icon: "fa-solid fa-star",
          color: "#8b5cf6",
          trend: null,
          trendIcon: null,
          trendColor: null,
          comingSoon: false,
          navigateTo: "/ipromiseafrica/resale/seller-dashboard/my_reviews",
        },
      ];
    },
    
    approvalStatusClass() {
      switch(this.seller.approvalStatus) {
        case 'approved': return 'status-approved';
        case 'pending': return 'status-pending';
        case 'rejected': return 'status-rejected';
        default: return 'status-unknown';
      }
    },
    
    approvalIcon() {
      switch(this.seller.approvalStatus) {
        case 'approved': return 'fa-solid fa-check-circle';
        case 'pending': return 'fa-solid fa-clock';
        case 'rejected': return 'fa-solid fa-times-circle';
        default: return 'fa-solid fa-question-circle';
      }
    },
    
    approvalStatusText() {
      switch(this.seller.approvalStatus) {
        case 'approved': return 'Approved';
        case 'pending': return 'Pending Review';
        case 'rejected': return 'Rejected';
        default: return 'Status Unknown';
      }
    }
  },

  mounted() {
    AOS.init({
      duration: 600,
      once: true,
      offset: 100
    });
    
    this.activeTab = this.$route.path;
    this.fetchSellerProfile();
    this.fetchDashboardStats();
    this.fetchListingStats();
  },

  methods: {
    formatNumber(num) {
      return Number(num).toLocaleString('en-KE');
    },
    
    openTab(route) {
      this.activeTab = route;
      this.$router.push(route);
    },

    onStatsCardClick(item) {
      if (item.comingSoon) return;
      if (item.navigateTo) {
        this.openTab(item.navigateTo);
      }
    },

    onDashboardTabClick(tab) {
      if (tab.comingSoon) {
        this.showNotificationMessage(
          "info",
          "Coming soon",
          "Messaging coming soon."
        );
        return;
      }
      this.openTab(tab.route);
    },
    
    goToProfileEdit() {
      if (this.seller.sellerNumber) {
        this.$router.push({ name: "SellerProfileEdit", params: { seller_number: this.seller.sellerNumber } });
      } else {
        this.showNotificationMessage("warning", "Profile", "Seller profile is not loaded yet. Try refreshing.");
      }
    },

    goToSettings() {
      this.$router.push({ name: "SellerSettings" });
    },
    
    handleImageError(event) {
      event.target.src = this.defaultProfilePicture;
    },
    
    async refreshDashboard() {
      this.loading = true;
      await this.fetchSellerProfile();
      await this.fetchDashboardStats();
      await this.fetchListingStats();
      this.showNotificationMessage('success', 'Dashboard Refreshed', 'Your dashboard data has been updated');
    },
    
    showNotificationMessage(type, title, message) {
      const icons = {
        success: 'fa-solid fa-check-circle',
        error: 'fa-solid fa-exclamation-circle',
        info: 'fa-solid fa-info-circle',
        warning: 'fa-solid fa-triangle-exclamation'
      };
      
      this.notification = {
        type,
        title,
        message,
        icon: icons[type]
      };
      
      this.showNotification = true;
      
      // Auto-hide after 5 seconds
      setTimeout(() => {
        this.showNotification = false;
      }, 5000);
    },

    async fetchSellerProfile() {
      const token = localStorage.getItem("access_token");
      if (!token) {
        this.profileLoadError = true;
        this.loading = false;
        this.showNotificationMessage('error', 'Authentication Required', 'Please log in to access the seller dashboard.');
        return;
      }
      try {
        const res = await fetch(buildApiUrl("/seller/me"), {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          if (res.status === 404) {
            this.loading = false;
            this.$router.push("/ipromiseafrica/resale/seller-sign-up");
            return;
          }
          throw new Error(`Failed to fetch seller profile (${res.status})`);
        }

        const data = await res.json().catch(() => ({}));
        this.profileLoadError = false;
        this.seller = {
          name: data.name || "Unknown Seller",
          profilePicture: data.profile_picture || this.defaultProfilePicture,
          sellerNumber: data.seller_number || "N/A",
          approvalStatus: data.approval_status?.toLowerCase() || "unknown"
        };

        if (this.seller.approvalStatus === 'pending') {
          this.showNotificationMessage(
            'warning',
            'Account Under Review',
            'Your seller account is currently being reviewed. You can still add products, but they will be visible only after approval.'
          );
        }
      } catch (err) {
        console.error("Error fetching seller profile:", err);
        this.profileLoadError = true;
        this.showNotificationMessage('error', 'Profile Error', err?.message || 'Could not load seller profile. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    retryLoad() {
      this.profileLoadError = false;
      this.loading = true;
      this.fetchSellerProfile();
    },

    async fetchDashboardStats() {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) return;

        const res = await fetch(buildApiUrl("/seller/dashboard-stats"), {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          this.stats = {
            active: data.active ?? 0,
            pending: data.pending ?? 0,
            earnings: data.earnings ?? 0,
            orders: data.orders ?? 0,
            views: data.views ?? 0,
          };
        } else {
          this.stats = { active: 0, pending: 0, earnings: 0, orders: 0, views: 0 };
        }
      } catch (err) {
        console.error("Error fetching dashboard stats:", err);
        this.stats = { active: 0, pending: 0, earnings: 0, orders: 0, views: 0 };
      } finally {
        this.loading = false;
      }
    },

    async fetchListingStats() {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) return;

        const res = await fetch(buildApiUrl("seller/listings/all"), {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) return;

        const data = await res.json();
        const payload = Array.isArray(data) ? data : (data.listings || data);

        const activeUnsold = payload.filter(l => !l.is_sold).length;
        const totalViews = payload.reduce((sum, l) => sum + (l.views || 0), 0);

        this.listingStats = {
          activeUnsold,
          totalViews,
        };
      } catch (err) {
        console.error("Error fetching listing stats:", err);
      }
    },
  },
};
</script>

<style scoped> 
/* === GLOBAL STYLES === */
.dashboard {
  background: var(--dashboard-bg);
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  box-sizing: border-box;
}

.dashboard-content {
  padding: 110px 20px 80px;
  padding-left: max(20px, env(safe-area-inset-left));
  padding-right: max(20px, env(safe-area-inset-right));
  padding-bottom: max(80px, env(safe-area-inset-bottom));
  max-width: 1400px;
  margin: 0 auto;
  color: var(--text-color1);
  box-sizing: border-box;
}

/* === DASHBOARD ERROR STATE (no default-value page) === */
.dashboard-error-state {
  background: var(--card-bg);
  border-radius: 24px;
  padding: 48px 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
}

.dashboard-error-state .error-content {
  max-width: 400px;
  margin: 0 auto;
}

.dashboard-error-state .error-icon {
  font-size: 3rem;
  color: var(--pending-color);
  margin-bottom: 20px;
}

.dashboard-error-state h3 {
  margin: 0 0 12px 0;
  font-size: 1.35rem;
  color: var(--heading-color);
}

.dashboard-error-state p {
  margin: 0 0 24px 0;
  color: var(--subtext-color);
  font-size: 0.95rem;
}

.dashboard-error-state .retry-btn,
.dashboard-error-state .back-btn {
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 8px 8px 0;
  transition: all 0.2s;
  border: none;
  font-size: 0.95rem;
}

.dashboard-error-state .retry-btn {
  background: var(--border-color-hover);
  color: var(--text-color3);
}

.dashboard-error-state .retry-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.dashboard-error-state .back-btn {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-color1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.dashboard-error-state .back-btn:hover {
  background: rgba(255, 255, 255, 0.15);
}

/* === LOADING OVERLAY === */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: max(20px, env(safe-area-inset-top)) max(20px, env(safe-area-inset-right)) max(20px, env(safe-area-inset-bottom)) max(20px, env(safe-area-inset-left));
  background: var(--light-overlay);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
  box-sizing: border-box;
}

.loading-content {
  background: var(--light-card);
  padding: 40px;
  border-radius: 20px;
  text-align: center;
  max-width: 400px;
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  box-sizing: border-box;
}

.loader {
  width: 60px;
  height: 60px;
  border: 4px solid var(--loader-border);
  border-radius: 50%;
  border-top-color: var(--loader-color);
  margin: 0 auto 20px;
  animation: spin 1s linear infinite;
}

.loading-content h3 {
  margin: 0 0 10px 0;
  color: var(--heading-color);
  font-size: 1.3rem;
}

.loading-content p {
  margin: 0;
  color: var(--subtext-color);
  font-size: 0.95rem;
}

/* === PROFILE HEADER === */
.profile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--card-bg);
  padding: 28px;
  border-radius: 24px;
  margin-bottom: 40px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
}

.profile-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--gradient-border);
  border-radius: 24px 24px 0 0;
}

.profile-info {
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
}

.profile-avatar {
  position: relative;
  width: 100px;
  height: 100px;
}

.profile-img {
  width: 100%;
  height: 100%;
  border-radius: 20px;
  object-fit: cover;
  border: 3px solid var(--profile-border);
  box-shadow: 0 8px 20px var(--profile-shadow);
  transition: all 0.3s ease;
}

.profile-img:hover {
  transform: scale(1.05);
  box-shadow: 0 12px 30px var(--profile-shadow);
}

.online-indicator {
  position: absolute;
  bottom: 5px;
  right: 5px;
  width: 16px;
  height: 16px;
  background: var(--online-indicator);
  border-radius: 50%;
  border: 3px solid var(--background-color2);
  animation: pulse 2s infinite ease-in-out;
}

.profile-details {
  flex: 1;
}

.seller-name {
  font-size: 1.8rem;
  font-weight: 800;
  margin: 0 0 10px 0;
  background: var(--name-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.profile-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
}

.market-charges {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  color: var(--subtext-color);
  padding: 4px 12px;
  background: var(--meta-bg);
  border-radius: 8px;
}

.market-charges i {
  color: var(--role-icon);
}

.approval-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-top: 8px;
  transition: all 0.3s ease;
}

.status-approved {
  background: var(--approved-bg);
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}

.status-pending {
  background: var(--pending-bg);
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
  animation: pulseStatus 2s infinite;
}

.status-rejected {
  background: var(--rejected-bg);
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.status-unknown {
  background: var(--unknown-bg);
  color: var(--unknown-color);
  border: 1px solid var(--unknown-border);
}

.status-note {
  font-size: 0.8rem;
  opacity: 0.8;
  margin-left: 4px;
}

.profile-actions {
  display: flex;
  align-items: center;
  gap: 20px;
}

.online-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  width: 10px;
  height: 10px;
  background: var(--online-indicator);
  border-radius: 50%;
  animation: pulse 1.5s infinite ease-in-out;
}

.edit-btn,
.settings-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn {
  padding: 12px 30px;
  border-radius: 32px;
  cursor: pointer;
  border: none;
  font-weight: 600;
  transition: 0.3s ease;
  font-size: clamp(0.95rem, 1.8vw, 1.1rem);
  font-family: inherit;
}

.btn.primary {
  background: var(--border-color-hover);
  color: var(--text-color3);
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.25);
}

.btn.primary:hover {
  transform: translateY(-3px);
  box-shadow: 0px 6px 18px rgba(0, 0, 0, 0.35);
}

.btn.outline {
  background: transparent;
  border: 2px solid var(--border-color-hover);
  color: var(--text-color1);
}

.btn.outline:hover {
  background: color-mix(in srgb, var(--border-color-hover) 12%, transparent);
  transform: translateY(-3px);
}

.dashboard-btn {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--heading-color);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dashboard-btn:hover {
  background: rgba(59, 130, 246, 0.2);
  transform: rotate(45deg);
}

/* === STATS GRID === */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 250px), 1fr));
  gap: 24px;
  margin-bottom: 40px;
}

.stats-card {
  background: var(--card-bg);
  padding: 24px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
}

.stats-card--disabled {
  cursor: not-allowed;
  pointer-events: auto;
}

.stats-card--disabled:hover {
  transform: none;
  box-shadow: none;
  border-color: rgba(255, 255, 255, 0.1);
}

.stats-card--disabled .stats-card-inner {
  filter: blur(5px);
  pointer-events: none;
  user-select: none;
}

.stats-card--nav {
  cursor: pointer;
}

.stats-card--nav:focus-visible {
  outline: 2px solid var(--border-color-hover);
  outline-offset: 3px;
}

.stats-card--nav:hover {
  border-color: var(--card-hover-border);
}

.stats-card-overlay--reviews {
  opacity: 0;
  background: rgba(0, 0, 0, 0.45);
  flex-direction: column;
  gap: 6px;
  pointer-events: none;
}

.stats-card--nav:hover .stats-card-overlay--reviews {
  opacity: 1;
}

.stats-card-overlay--reviews .coming-soon-text {
  font-size: 1.15rem;
}

.reviews-chevron {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.95);
}

.stats-card-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 20px;
  opacity: 0;
  transition: opacity 0.25s ease;
  z-index: 1;
}

.stats-card--disabled:hover .stats-card-overlay {
  opacity: 1;
}

.coming-soon-text {
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.stats-card:not(.stats-card--disabled):hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border-color: var(--card-hover-border);
}

.stats-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: inherit;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.stats-card:not(.stats-card--disabled):hover::before {
  opacity: 1;
}

.stats-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 1.5rem;
}

.stats-content {
  margin-bottom: 16px;
}

.stats-value {
  font-size: 2rem;
  font-weight: 800;
  margin: 0 0 8px 0;
  color: var(--heading-color);
}

.stats-label {
  color: var(--subtext-color);
  margin: 0;
  font-size: 0.95rem;
}

.stats-sublabel {
  font-size: 0.8rem;
  color: var(--subtext-color);
  opacity: 0.9;
  margin: 2px 0 0 0;
}

.stats-trend {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  font-weight: 600;
}

/* === QUICK ACTIONS === */
.quick-actions {
  margin-bottom: 40px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0 0 24px 0;
  color: var(--heading-color);
}

.section-title i {
  color: var(--section-icon);
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: 20px;
}

.action-card {
  background: var(--card-bg);
  padding: 24px;
  min-height: 140px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  color: inherit;
  touch-action: manipulation;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.action-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
}

.action-icon {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 1.5rem;
}

.action-icon.primary {
  background: var(--action-primary-bg);
  color: var(--action-primary-color);
}

.action-icon.secondary {
  background: var(--action-secondary-bg);
  color: var(--action-secondary-color);
}

.action-icon.success {
  background: var(--action-success-bg);
  color: var(--action-success-color);
}

.action-icon.warning {
  background: var(--action-warning-bg);
  color: var(--action-warning-color);
}

.action-card h4 {
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  color: var(--heading-color);
}

.action-card p {
  margin: 0;
  color: var(--subtext-color);
  font-size: 0.9rem;
}

/* === DASHBOARD TABS === */
.dashboard-tabs {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  margin-bottom: 40px;
}

.tabs-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  margin-bottom: 16px;
}

.tabs-header .section-title {
  margin: 0;
  width: 100%;
}

.tabs-indicator {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.indicator-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.indicator-dot.active {
  background: var(--section-icon);
  transform: scale(1.2);
}

.tabs-container {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(12px, 1.5vw, 20px);
  align-items: stretch;
  justify-items: stretch;
  width: 100%;
}

.tab-card {
  appearance: none;
  -webkit-appearance: none;
  background: var(--card-bg);
  padding: 20px 18px;
  min-height: 0;
  min-width: 0;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 12px;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  text-align: left;
  color: inherit;
  font-family: inherit;
  white-space: normal;
  touch-action: manipulation;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.tab-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
  border-color: color-mix(in srgb, var(--border-color-hover) 35%, transparent);
}

.tab-card:focus-visible {
  outline: 2px solid var(--border-color-hover);
  outline-offset: 3px;
}

.tab-card--no-fx:hover {
  transform: none;
  box-shadow: none;
}

.tab-card--no-fx:hover .tab-arrow {
  transform: none;
}

.tab-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: var(--tab-icon-bg);
  color: var(--tab-icon-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  flex-shrink: 0;
}

.tab-content {
  min-width: 0;
  width: 100%;
}

.tab-content h4 {
  margin: 0 0 4px 0;
  font-size: 1rem;
  line-height: 1.3;
  color: var(--heading-color);
  overflow-wrap: break-word;
}

.tab-content p {
  margin: 0;
  color: var(--subtext-color);
  font-size: 0.85rem;
  line-height: 1.4;
  overflow-wrap: break-word;
}

.tab-arrow {
  display: none;
}

/* Three columns get tight once each card no longer has room for the label */
@media (max-width: 1100px) {
  .tabs-container {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Two columns squeeze titles and descriptions on phones */
@media (max-width: 680px) {
  .tabs-container {
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
  }

  .tab-card {
    padding: 14px;
    gap: 12px;
  }
}

/* === RECENT ACTIVITY === */
.recent-activity {
  margin-bottom: 60px;
}

.activity-placeholder {
  background: var(--card-bg);
  border: 2px dashed rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 60px 20px;
  text-align: center;
  color: var(--subtext-color);
}

.activity-placeholder i {
  font-size: 3rem;
  margin-bottom: 16px;
  color: var(--placeholder-icon);
}

.activity-placeholder p {
  margin: 0 0 8px 0;
  font-size: 1.1rem;
}

.activity-placeholder small {
  font-size: 0.9rem;
  opacity: 0.7;
}

/* === NOTIFICATION === */
.notification-popup {
  position: fixed;
  bottom: max(20px, env(safe-area-inset-bottom));
  right: max(20px, env(safe-area-inset-right));
  left: max(20px, env(safe-area-inset-left));
  z-index: 1000;
  min-width: 0;
  max-width: 400px;
  margin-left: auto;
  background: var(--notification-bg);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  animation: slideInRight 0.5s ease-out;
  border-left: 4px solid;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.notification-popup.success {
  border-left-color: var(--notification-success-border);
}

.notification-popup.error {
  border-left-color: var(--notification-error-border);
}

.notification-popup.info {
  border-left-color: var(--notification-info-border);
}

.notification-popup.warning {
  border-left-color: var(--notification-warning-border);
}

.notification-content {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  position: relative;
}

.notification-content i {
  font-size: 1.5rem;
  margin-top: 2px;
}

.notification-popup.success .notification-content i {
  color: var(--notification-success-border);
}

.notification-popup.error .notification-content i {
  color: var(--notification-error-border);
}

.notification-popup.info .notification-content i {
  color: var(--notification-info-border);
}

.notification-popup.warning .notification-content i {
  color: var(--notification-warning-border);
}

.notification-text {
  flex: 1;
}

.notification-text h4 {
  margin: 0 0 5px 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--heading-color);
}

.notification-text p {
  margin: 0;
  font-size: 0.95rem;
  color: var(--subtext-color);
  line-height: 1.4;
}

.close-notification {
  position: absolute;
  top: -10px;
  right: -10px;
  min-width: 44px;
  min-height: 44px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--subtext-color);
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  touch-action: manipulation;
}

.close-notification:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--heading-color);
}

/* === ANIMATIONS === */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideInRight {
  from { opacity: 0; transform: translateX(100%); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
}

@keyframes pulseStatus {
  0% { box-shadow: 0 0 0 0 var(--status-pulse); }
  70% { box-shadow: 0 0 0 10px rgba(245, 158, 11, 0); }
  100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
}

/* === RESPONSIVE DESIGN === */
@media (max-width: 1024px) {
  .profile-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
  }
  
  .profile-actions {
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
  }
}

@media (max-width: 768px) {
  .dashboard-content {
    padding-top: 90px;
    padding-left: max(15px, env(safe-area-inset-left));
    padding-right: max(15px, env(safe-area-inset-right));
    padding-bottom: max(60px, env(safe-area-inset-bottom));
  }
  
  .profile-header {
    padding: 20px;
    border-radius: 20px;
  }
  
  .profile-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .profile-avatar {
    width: 80px;
    height: 80px;
  }
  
  .seller-name {
    font-size: 1.5rem;
  }
  
  .section-title {
    font-size: 1.2rem;
    margin-bottom: 20px;
  }
  
  .tabs-header {
    align-items: flex-start;
    width: 100%;
    gap: 10px;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin-bottom: 32px;
  }
  
  .actions-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  
  .action-card {
    min-height: 120px;
    padding: 18px;
  }
  
  .action-card h4 {
    font-size: 1rem;
  }
  
  .action-card p {
    font-size: 0.85rem;
  }
  
  .notification-popup {
    left: max(15px, env(safe-area-inset-left));
    right: max(15px, env(safe-area-inset-right));
    bottom: max(15px, env(safe-area-inset-bottom));
  }
  
  .btn-text {
    display: none;
  }
  
  .edit-btn,
  .settings-btn {
    padding: 10px 16px;
    min-width: 44px;
    min-height: 44px;
    justify-content: center;
    touch-action: manipulation;
  }
  
  .dashboard-btn {
    min-width: 44px;
    min-height: 44px;
    touch-action: manipulation;
  }
}

@media (max-width: 480px) {
  .dashboard-content {
    padding-top: 85px;
    padding-left: max(12px, env(safe-area-inset-left));
    padding-right: max(12px, env(safe-area-inset-right));
  }
  
  .profile-header {
    padding: 16px;
    border-radius: 16px;
    margin-bottom: 24px;
  }
  
  .profile-actions {
    gap: 10px;
  }
  
  .online-status p {
    font-size: 0.85rem;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 24px;
  }
  
  .actions-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .action-card {
    min-height: 110px;
    padding: 20px 16px;
  }
  
  .quick-actions,
  .dashboard-tabs {
    margin-bottom: 24px;
  }
  
  .profile-meta {
    flex-direction: column;
    gap: 8px;
  }
  
  .approval-status {
    flex-wrap: wrap;
    font-size: 0.85rem;
    padding: 6px 12px;
  }
  
  .status-note {
    display: block;
    width: 100%;
    margin-left: 0;
    margin-top: 4px;
  }
  
  .stats-card {
    padding: 18px;
  }

  .tab-card {
    padding: 12px;
    gap: 10px;
    border-radius: 16px;
  }
  
  .stats-value {
    font-size: 1.75rem;
  }
  
  .stats-icon {
    width: 48px;
    height: 48px;
    font-size: 1.25rem;
    margin-bottom: 12px;
  }
  
  .tab-icon {
    width: 42px;
    height: 42px;
    font-size: 1rem;
    border-radius: 12px;
  }
  
  .tab-content h4 {
    font-size: 0.95rem;
  }
  
  .tab-content p {
    font-size: 0.8rem;
  }
  
  .activity-placeholder {
    padding: 40px 16px;
  }
  
  .activity-placeholder i {
    font-size: 2.5rem;
  }
}

/* Very small phones */
@media (max-width: 360px) {
  .dashboard-content {
    padding-left: max(10px, env(safe-area-inset-left));
    padding-right: max(10px, env(safe-area-inset-right));
  }
  
  .profile-header {
    padding: 12px;
  }
  
  .profile-avatar {
    width: 64px;
    height: 64px;
  }
  
  .seller-name {
    font-size: 1.25rem;
  }
  
  .section-title {
    font-size: 1.1rem;
  }
  
  .stats-value {
    font-size: 1.5rem;
  }
  
  .action-card h4 {
    font-size: 0.95rem;
  }
  
  .stats-card,
  .action-card {
    padding: 14px;
  }

  .tab-card {
    padding: 12px 10px;
  }
}

/* === UTILITY CLASSES === */
@media (max-width: 768px) {
  .hide-on-mobile {
    display: none;
  }
}

@media (min-width: 769px) {
  .hide-on-desktop {
    display: none;
  }
}
</style>